import { ConversationChannelValue, EventTypeValue } from '@vendasta/protos/conversation/v1';

/**
 * Text exists only on `resolved`, which is constructible only where the email service returned
 * content; nothing can read a subject past a gate that did not run.
 *
 * `withheld` also covers a lookup that did not complete. Email cannot tell the two apart: a
 * namespace-level IAM denial fails the whole RPC exactly as a network error does, so a real
 * permission decision arrives here as an error. Splitting them means guessing, and guessing wrong
 * on the permission case. One outcome, matching the CRM activity card, until the API can say.
 */
export type PersonalEmailPreview = { kind: 'resolved'; subject: string; snippet: string } | { kind: 'withheld' };

/** Nothing to look up. Resolved with no text, so the card keeps its channel sentence. */
export const NO_PERSONAL_EMAIL_PREVIEW: PersonalEmailPreview = Object.freeze({
  kind: 'resolved',
  subject: '',
  snippet: '',
});

export const WITHHELD_PERSONAL_EMAIL_PREVIEW: PersonalEmailPreview = Object.freeze({ kind: 'withheld' });

/**
 * A ceiling on the raw body before anything parses it, not on what is shown; the card bounds its own
 * height in CSS. Applied first so no card pays to parse a whole mail body, which it otherwise would
 * twice: once to reduce it to preview markup and again to check that markup for text.
 *
 * Generous because it counts markup rather than words, and so far past the few lines a card shows
 * that a cut through a tag lands well outside what anyone sees; the parser closes whatever it left open.
 */
export const PERSONAL_EMAIL_SNIPPET_MAX_CODE_POINTS = 10000;

/**
 * Identifies the event a personal email conversation puts on its inbox row to point at the
 * threaded conversation holding the messages. SMS and contact merges write the same event type,
 * and a merge stamps an initiator where a thread event does not.
 *
 * Asks whether the event is that one, not whether it may supply preview text, so it omits the
 * non-empty-message condition the conversation service applies in CanPreview. Do not unify them:
 * the card gates its snippet on this, and a subjectless conversation is a committed card state.
 */
export function isPersonalEmailThreadEvent(
  event: { type?: number; channel?: number; initiatorId?: string } | undefined | null,
): boolean {
  return (
    event?.type === EventTypeValue.EVENT_TYPE_DUPLICATE_CONVERSATION &&
    event.channel === ConversationChannelValue.CONVERSATION_CHANNEL_PERSONAL_EMAIL &&
    !event.initiatorId
  );
}

/**
 * Metadata key on the stub event holding the threaded conversation that actually carries the
 * messages. Named "dead" because the thread is a UI orphan: it is reachable only through this key.
 */
export const EVENT_METADATA_DEAD_CONVERSATION_ID_KEY = 'event_dead_conversation_id';

/** Shape of a thread event this module reads; the generated Event satisfies it structurally. */
interface ThreadEventLike {
  type?: number;
  channel?: number;
  initiatorId?: string;
  happenedAt?: Date;
  metadata?: { key?: string; value?: string }[];
}

/**
 * Id of the threaded conversation behind the newest personal email thread event on a main
 * conversation.
 *
 * A main conversation belongs to a contact rather than a thread, so a contact who has written
 * several times leaves several thread events on it and the newest one is the thread the row should
 * describe. Merge events are passed over by `isPersonalEmailThreadEvent`. Returns '' when the
 * conversation carries no email thread event, which is every non-email row and every conversation
 * whose only such events came from a merge.
 */
export function newestEmailThreadId(events: ThreadEventLike[] | undefined | null): string {
  const candidates = (events ?? []).filter(isPersonalEmailThreadEvent).map((event) => ({
    at: event.happenedAt?.getTime() ?? 0,
    threadId: event.metadata?.find((meta) => meta.key === EVENT_METADATA_DEAD_CONVERSATION_ID_KEY)?.value ?? '',
  }));

  // Thread id breaks a tie rather than array order: the events arrive in the order the API returned
  // them, so ties would otherwise pick a different subject between loads.
  const newest = candidates.reduce<{ at: number; threadId: string } | undefined>(
    (best, candidate) =>
      !best || candidate.at > best.at || (candidate.at === best.at && candidate.threadId > best.threadId)
        ? candidate
        : best,
    undefined,
  );

  return newest?.threadId ?? '';
}
